import { useRef } from "react";
import { motion, useMotionValue, useScroll, useSpring, useTransform } from "framer-motion";
import { ArrowDownRight } from "lucide-react";
import { CATEGORIES } from "@/data/menu";
import { scrollToId } from "@/lib/scroll";

const EASE = [0.22, 1, 0.36, 1];
const LINES = [
  { t: "Little luxuries,", c: "" },
  { t: "hand-dipped", c: "italic text-rose" },
  { t: "& made to order.", c: "" },
];
const FLAVORS = CATEGORIES.reduce((s, c) => s + (c.items.length || 13), 0);

const SPRINKLES = [
  ["#F472B6", "8%", "18%", -30, 0], ["#F59E0B", "46%", "10%", 20, 0.4], ["#34D399", "88%", "30%", -60, 0.8],
  ["#60A5FA", "52%", "86%", 45, 0.2], ["#E11D48", "4%", "78%", 70, 0.6], ["#F472B6", "94%", "72%", -15, 1],
];

const MaskLine = ({ children, i, className }) => (
  <span className="block overflow-hidden pb-[0.08em]">
    <motion.span
      className={`block ${className}`}
      initial={{ y: "105%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.1, delay: 0.15 + i * 0.12, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

const Badge = () => (
  <div className="spin-slow absolute -left-6 bottom-10 z-20 h-28 w-28 sm:-left-10 sm:h-36 sm:w-36" data-testid="hero-rotating-badge">
    <svg viewBox="0 0 200 200" className="h-full w-full">
      <circle cx="100" cy="100" r="98" fill="#2D1E18" />
      <defs>
        <path id="circ" d="M100,100 m-70,0 a70,70 0 1,1 140,0 a70,70 0 1,1 -140,0" />
      </defs>
      <text fill="#FCE8E6" fontSize="17" letterSpacing="5" fontFamily="JetBrains Mono">
        <textPath href="#circ">MADE TO ORDER • HAND DIPPED • SWEET •</textPath>
      </text>
      <path d="M100 128 l-22-22a14 14 0 0 1 22-18a14 14 0 0 1 22 18z" fill="#E11D48" />
    </svg>
  </div>
);

const HeroVisual = () => {
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [8, -8]), { stiffness: 120, damping: 14 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-10, 10]), { stiffness: 120, damping: 14 });
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const y2 = useTransform(scrollYProgress, [0, 1], [0, -80]);

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <div ref={ref} className="relative mx-auto w-full max-w-[520px] [perspective:1200px]" onMouseMove={onMove} onMouseLeave={() => { mx.set(0); my.set(0); }}>
      <motion.div
        initial={{ clipPath: "inset(100% 0 0 0)", scale: 1.08 }}
        animate={{ clipPath: "inset(0% 0 0 0)", scale: 1 }}
        transition={{ duration: 1.4, delay: 0.35, ease: EASE }}
        style={{ rotateX: rx, rotateY: ry, y }}
        className="arch relative aspect-[4/5] w-full overflow-hidden shadow-[0_40px_80px_-30px_rgba(45,30,24,0.45)] [transform-style:preserve-3d]"
      >
        <img src="/img/cakepops2.jpg" alt="Chocolate-dipped cake pops" className="h-full w-full object-cover" data-testid="hero-image" />
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_50%_30%,transparent_40%,rgba(45,30,24,0.35))]" />
      </motion.div>
      <motion.div
        style={{ y: y2 }}
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 1, delay: 0.9, ease: EASE }}
        className="absolute -right-3 top-8 z-20 h-32 w-32 overflow-hidden rounded-full border-[6px] border-[#FAF6F0] shadow-xl sm:-right-8 sm:h-44 sm:w-44"
      >
        <img src="/img/strawberries.jpg" alt="Chocolate-covered strawberries" className="h-full w-full object-cover" />
      </motion.div>
      <Badge />
    </div>
  );
};

export const Hero = () => (
  <section className="grain relative overflow-hidden bg-cream pb-16 pt-32 sm:pt-40 lg:pb-28" data-testid="hero-section">
    {SPRINKLES.map(([col, l, t, r, d], i) => (
      <motion.span
        key={i}
        className="absolute hidden h-2.5 w-7 rounded-full sm:block"
        style={{ background: col, left: l, top: t, rotate: r }}
        animate={{ y: [0, -14, 0] }}
        transition={{ duration: 5 + i, repeat: Infinity, ease: "easeInOut", delay: d }}
      />
    ))}
    <div className="relative mx-auto grid max-w-[1400px] items-center gap-14 px-5 sm:px-8 lg:grid-cols-12">
      <div className="lg:col-span-7">
        <motion.p
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="f-mono mb-8 flex flex-wrap items-center gap-3 text-xs uppercase tracking-[0.25em] text-cacao-2"
        >
          <span className="h-2 w-2 rounded-full bg-[#E11D48]" /> Online order menu
          <span className="text-[#E11D48]">/</span> {CATEGORIES.length} collections
          <span className="text-[#E11D48]">/</span> {FLAVORS}+ flavors
        </motion.p>
        <h1 className="f-display text-[3.2rem] font-semibold leading-[0.95] tracking-tight text-cacao sm:text-7xl lg:text-[6.6rem]" data-testid="hero-title">
          {LINES.map((l, i) => (
            <MaskLine key={l.t} i={i} className={l.c}>{l.t}</MaskLine>
          ))}
        </h1>
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.7, ease: EASE }}
          className="mt-10 max-w-xl"
        >
          <p className="text-base leading-relaxed text-cacao-2 sm:text-lg">
            Cake pops, dipped strawberries, gooey giant cookies and custom dessert boxes. Pick exactly what you want, send
            it to us by text or email, and we'll reply with your quote.
          </p>
          <div className="mt-9 flex flex-wrap items-center gap-4">
            <motion.button
              whileHover={{ y: -3 }}
              whileTap={{ scale: 0.97 }}
              onClick={() => scrollToId("menu")}
              data-testid="hero-build-box-button"
              className="group flex items-center gap-3 rounded-full bg-[#E11D48] px-7 py-4 text-sm font-semibold text-white shadow-[0_14px_30px_-12px_rgba(225,29,72,0.7)]"
            >
              Start your order
              <ArrowDownRight className="h-4 w-4 transition-transform duration-300 group-hover:rotate-[-45deg]" />
            </motion.button>
            <button
              onClick={() => scrollToId("how")}
              data-testid="hero-how-it-works-button"
              className="rounded-full border border-[#2D1E18]/20 px-7 py-4 text-sm font-semibold text-cacao transition-colors duration-300 hover:bg-[#2D1E18] hover:text-[#FAF6F0]"
            >
              How ordering works
            </button>
          </div>
        </motion.div>
      </div>
      <div className="lg:col-span-5">
        <HeroVisual />
      </div>
    </div>
  </section>
);
